/**
 * apps/web :: pass :: screens/RulePack
 *
 * Offline rule list for the active country pack (ROADS-569 W1.C2).
 * Reads the traveler's first active `tourist_out` country (fallback "IT"),
 * resolves its pack, and renders every rule grouped by category. Each row
 * carries a severity dot (high=red / medium=amber / info=green), the title
 * + detail, an optional cost chip, an external "source" link to the rule's
 * citation, and a "Last reviewed …" badge so travelers can trust recency.
 *
 * Wrapped in PassLayout for consistent pass chrome.
 */
import PassLayout from "../PassLayout";
import {
  getPack,
  useActivePassCountries,
  type PackId,
  type Rule,
  type RuleCategory,
  type Severity,
} from "@/lib/pass-content";

/** Human labels for each rule category (group headings). */
const CATEGORY_LABELS: Record<RuleCategory, string> = {
  zone: "Zones",
  vignette_toll: "Vignette & tolls",
  equipment: "Equipment",
  limit: "Speed & limits",
  parking: "Parking",
  emergency: "Emergency",
  document: "Documents",
};

/** Tailwind dot colour per severity. */
const SEVERITY_DOT: Record<Severity, string> = {
  high: "bg-red-500",
  medium: "bg-amber-500",
  info: "bg-green-500",
};

/** Stable display order for the category groups. */
const CATEGORY_ORDER: RuleCategory[] = [
  "zone",
  "limit",
  "vignette_toll",
  "parking",
  "equipment",
  "document",
  "emergency",
];

/** Group rules by category, preserving CATEGORY_ORDER. */
function groupByCategory(rules: Rule[]): { category: RuleCategory; rules: Rule[] }[] {
  const buckets = new Map<RuleCategory, Rule[]>();
  for (const rule of rules) {
    const list = buckets.get(rule.category) ?? [];
    list.push(rule);
    buckets.set(rule.category, list);
  }
  const groups: { category: RuleCategory; rules: Rule[] }[] = [];
  for (const category of CATEGORY_ORDER) {
    const list = buckets.get(category);
    if (list && list.length > 0) groups.push({ category, rules: list });
  }
  return groups;
}

export default function RulePack(): JSX.Element {
  const country = useActivePassCountries()[0] ?? "IT";
  const pack = getPack(country as PackId);
  const groups = groupByCategory(pack.rules);

  return (
    <PassLayout country={country} title={`${pack.jurisdiction.name} road rules`}>
      <p className="text-sm text-neutral-700">
        The local rules that catch travelers out — saved offline for your trip.
      </p>

      {groups.map((group) => (
        <section
          key={group.category}
          className="mt-lg"
          data-testid={`rule-group-${group.category}`}
        >
          <h2 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            {CATEGORY_LABELS[group.category]}
          </h2>
          <ul className="mt-sm flex flex-col gap-2">
            {group.rules.map((rule) => (
              <li
                key={rule.id}
                data-testid={`rule-row-${rule.id}`}
                className="rounded-md border border-neutral-200 px-md py-sm"
              >
                <div className="flex items-start gap-2">
                  <span
                    aria-hidden="true"
                    data-testid={`rule-severity-${rule.id}`}
                    data-severity={rule.severity}
                    className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${SEVERITY_DOT[rule.severity]}`}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-neutral-900">
                      {rule.title}
                    </p>
                    <p className="mt-0.5 text-sm text-neutral-600">
                      {rule.detail}
                    </p>

                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      {rule.cost ? (
                        <span
                          data-testid={`rule-cost-${rule.id}`}
                          className="inline-flex items-center rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-semibold text-neutral-700"
                        >
                          {rule.cost}
                        </span>
                      ) : null}
                      <span
                        data-testid={`rule-reviewed-${rule.id}`}
                        className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
                      >
                        Last reviewed {rule.lastReviewed}
                      </span>
                      <a
                        href={rule.citation}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`rule-source-${rule.id}`}
                        className="text-xs font-semibold text-indigo-600 underline hover:text-indigo-800"
                      >
                        Source
                      </a>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </PassLayout>
  );
}
